import { PriceCheck, ShoppingCart, Storefront } from "@mui/icons-material";
import dayjs from '../../../utils/dayjs-setup';
import { Avatar, Box, Card, CardContent, CardHeader, GridLegacy as Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useState, useEffect, useContext } from "react";
import { AutoCompleteInputWithLabel, DatePickerInputWithLabel } from "../../../components/form/custom-imputs";
import { ActionTypes, AppContext } from "../../../store/AppStore";
import { AxiosInstance, DateFormatString, DateTimeFormatString, DialogType, FormatPrice } from "../../../utils/constants";
import * as yup from "yup";
import { FilterForm } from "../../../components/form/filter-form";
import { green, orange } from "@mui/material/colors";

const schemaFilter = yup.object({
  fecha_inicio: yup.mixed().required(),
  fecha_fin: yup.mixed().required().test(
    "_not_before",
    "La fecha debe ser igual o mayor a la de inicio",
    function (item) {
      if (!item || !this.parent.fecha_inicio) {
        return true;
      }
      return !dayjs(item).isBefore(dayjs(this.parent.fecha_inicio), "day");
    }
  ),
  comprador: yup.mixed().nullable()
}).required();

const emptyTotales = {
  total_ejecutado: 0,
  total_por_necesidad: 0,
  total_pantalla_libre: 0,
  pct_por_necesidad: 0,
  pct_pantalla_libre: 0
};

export const OrigenCompraComprador = ({
  fechaSeleccionada
}) => {
  const fechaBase = fechaSeleccionada ? dayjs(fechaSeleccionada) : dayjs();
  const initialFilter = {
    fecha_inicio: fechaBase.startOf("month").format(DateFormatString),
    fecha_fin: fechaBase.format(DateFormatString),
    comprador: null
  };

  const [compradores, setCompradores] = useState([]);
  const [dataSet, setDataSet] = useState([]);
  const [totales, setTotales] = useState({ ...emptyTotales });
  const [filterSearch, setFilterSearch] = useState(initialFilter);
  const { dispatch } = useContext(AppContext);

  useEffect(() => {
    loadInitData();
  }, []);

  useEffect(() => {
    loadItems();
  }, [filterSearch]);

  const loadInitData = async () => {
    try {
      let { data: { value: compradoresList } } = await AxiosInstance.get("maestros/listar-compradores");
      setCompradores([...(compradoresList || [])]);
    } catch (e) {
      setCompradores([]);
    }
  };

  const loadItems = async () => {
    dispatch({
      type: ActionTypes.setLoadingPage,
      data: true
    });

    try {
      const { data } = await AxiosInstance.post("auditoria/consultar-origen-compra-comprador", {
        fecha_inicio: dayjs(filterSearch.fecha_inicio).startOf("day").format(DateTimeFormatString),
        fecha_fin: dayjs(filterSearch.fecha_fin).endOf("day").format(DateTimeFormatString),
        comprador: filterSearch.comprador?.value || null
      });

      setDataSet(data?.value?.data || []);
      setTotales(data?.value?.totales || { ...emptyTotales });
    } catch (e) {
      dispatch({
        type: ActionTypes.setModalInfo,
        data: {
          title: "Error",
          message: "Error de comunicacion verifique su conexion a internet",
          type: DialogType.peligro,
          isVisible: true
        }
      });
      setDataSet([]);
      setTotales({ ...emptyTotales });
    } finally {
      dispatch({
        type: ActionTypes.setLoadingPage,
        data: false
      });
    }
  };

  const setFilterAply = (filtros) => {
    setFilterSearch({
      fecha_inicio: filtros.fecha_inicio,
      fecha_fin: filtros.fecha_fin,
      comprador: filtros.comprador || null
    });
  };

  const InputFilters = [
    {
      grid: {
        xs: 3,
        md: 3,
        sm: 3
      },
      InputComponent: DatePickerInputWithLabel,
      keyValue: "fecha_inicio",
      InputComponentProps: {
        label: "Fecha de inicio",
        fullWidth: true,
        size: "small",
        inputFormat: "DD-MM-YYYY"
      }
    },
    {
      grid: {
        xs: 3,
        md: 3,
        sm: 3
      },
      InputComponent: DatePickerInputWithLabel,
      keyValue: "fecha_fin",
      InputComponentProps: {
        label: "Fecha de corte",
        fullWidth: true,
        size: "small",
        inputFormat: "DD-MM-YYYY"
      }
    },
    {
      grid: {
        xs: 3,
        md: 3,
        sm: 3
      },
      InputComponent: AutoCompleteInputWithLabel,
      keyValue: "comprador",
      InputComponentProps: {
        label: "Comprador",
        fullWidth: true,
        isLoading: false,
        multiple: false,
        size: "small",
        options: compradores || [],
        noOptionsText: "Lista vacia"
      }
    }
  ];

  const columnsTable = [
    {
      key: "comprador",
      keyValue: "comprador",
      label: "Comprador",
      headerRender: (props) => props.label,
      bodyRender: (props) => props.value
    },
    {
      key: "total_ejecutado",
      keyValue: "total_ejecutado",
      label: "Total $",
      headerRender: (props) => props.label,
      bodyRender: (props) => `$ ${FormatPrice(props.value, 2)}`
    },
    {
      key: "total_por_necesidad",
      keyValue: "total_por_necesidad",
      label: "$ necesidad",
      headerRender: (props) => props.label,
      bodyRender: (props) => `$ ${FormatPrice(props.value, 2)}`
    },
    {
      key: "pct_por_necesidad",
      keyValue: "pct_por_necesidad",
      label: "% necesidad",
      headerRender: (props) => props.label,
      bodyRender: (props) => `${FormatPrice(props.value, 2)} %`
    },
    {
      key: "total_pantalla_libre",
      keyValue: "total_pantalla_libre",
      label: "$ libre",
      headerRender: (props) => props.label,
      bodyRender: (props) => `$ ${FormatPrice(props.value, 2)}`
    },
    {
      key: "pct_pantalla_libre",
      keyValue: "pct_pantalla_libre",
      label: "% libre",
      headerRender: (props) => props.label,
      bodyRender: (props) => `${FormatPrice(props.value, 2)} %`
    }
  ];

  return (
    <>
      <Grid container spacing={2}>
        <Grid container item sm={12} xs={12} md={12} spacing={1}>
          <Grid item sm={12} xs={12} md={4}>
            <Card>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: "success.main" }}>
                    <PriceCheck />
                  </Avatar>
                }
                title="Total ejecutado"
              />
              <CardContent>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h6">$ {FormatPrice(totales.total_ejecutado, 2)} COP</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item sm={12} xs={12} md={4}>
            <Card>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: green[600] }}>
                    <ShoppingCart />
                  </Avatar>
                }
                title="% por necesidad"
              />
              <CardContent>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h6">{FormatPrice(totales.pct_por_necesidad, 2)} %</Typography>
                  <Typography variant="caption">$ {FormatPrice(totales.total_por_necesidad, 2)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item sm={12} xs={12} md={4}>
            <Card>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: orange[700] }}>
                    <Storefront />
                  </Avatar>
                }
                title="% pantalla libre"
              />
              <CardContent>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h6">{FormatPrice(totales.pct_pantalla_libre, 2)} %</Typography>
                  <Typography variant="caption">$ {FormatPrice(totales.total_pantalla_libre, 2)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid item sm={12} xs={12} md={12}>
          <FilterForm
            buttomDist={{
              xs: 3,
              md: 3
            }}
            nameFilter={"Filtrar origen de compra por"}
            inputsFilter={InputFilters}
            schemaValidator={schemaFilter}
            initialValuesFilter={initialFilter}
            onApplyFilter={setFilterAply}
          />
        </Grid>

        <Grid item sm={12} xs={12} md={12}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {
                    columnsTable.map(colum => (
                      <TableCell key={colum.key}>
                        {<colum.headerRender label={colum.label} />}
                      </TableCell>
                    ))
                  }
                </TableRow>
              </TableHead>
              <TableBody>
                {dataSet.map((row) => (
                  <TableRow key={row.cod_usuario}>
                    {
                      columnsTable.map(Col => (
                        <TableCell key={Col.key}>
                          {<Col.bodyRender value={row[Col.keyValue]} info={row} />}
                        </TableCell>
                      ))
                    }
                  </TableRow>
                ))}
                {dataSet.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columnsTable.length}>
                      <Typography variant="body2">No hay compras confirmadas en el rango seleccionado</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </>
  );
};
